import {
  InvoiceSubmission,
  InvoiceSubmissionStatus,
  InvoicingSection,
  InvoicingCostItem,
  ItemStatus,
  InvoicingFixValues,
  GcpExtraCostType,
  GcpMaterialEntry,
} from './types';
import { AssociatedMission, Invoice, TimeMaterialsEntry, TimeMaterialsEntryPayload } from '../../../api_services/types';
import { Gold10, Gold90, Indigo10, Indigo90, Green0, Green90, Red10, Red90 } from '@traceair/tds';
import { numericOnly } from '../../../libs/numericOnly';

export { numericOnly };

export function getStatusBadgeProps(status: InvoiceSubmission['status']): Record<string, unknown> {
  switch (status) {
    case 'Fix':
      return { customStyles: { background: Gold10, color: Gold90 } };
    case 'Awaiting Payment':
      return { customStyles: { background: Indigo10, color: Indigo90 } };
    case 'Paid':
      return { customStyles: { background: Green0, color: Green90 } };
    case 'Cancelled':
      return { customStyles: { background: Red10, color: Red90 } };
    default:
      return {};
  }
}

export const CATEGORY_LABELS: Record<string, string> = {
  scan: 'Scans',
  pano: 'Additional Panoramas',
  lidar: 'LiDAR',
  gcp_services: 'GCP services',
  video: 'Videos & Images',
  other: 'Misc',
};

export const CATEGORY_ORDER = ['scan', 'pano', 'lidar', 'gcp_services', 'video', 'other'];

// Raw backend category values that share a single UI grouping ("category group") with
// another category rather than getting their own section — e.g. `additional-images` folds
// into the `video` group so both render under one "Videos & Images" section.
const CATEGORY_GROUP_ALIASES: Record<string, string> = {
  'additional-images': 'video',
};

export function getCategoryGroupId(category: string): string {
  return CATEGORY_GROUP_ALIASES[category] ?? category;
}

// The API sends calendar dates (startDate/endDate/flightDate) as naive "YYYY-MM-DD" strings with
// no time component. `new Date('YYYY-MM-DD')` parses that as UTC midnight, so formatting it with
// `toLocaleDateString()` (which renders in the browser's local zone) shifts the displayed day back
// by one for anyone west of UTC. Parsing the y/m/d components directly into a local-time `Date`
// avoids that UTC round-trip. Full timestamps (e.g. submittedAt/paidOn) aren't naive dates and are
// left to the normal `new Date(...)` path.
function parseNaiveDate(date: string | Date): Date {
  if (typeof date === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  return new Date(date);
}

export function formatApiDate(date: string | Date): string {
  const d = parseNaiveDate(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatApiDateShort(date: string | Date): string {
  const d = parseNaiveDate(date);
  const omitYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(omitYear ? {} : { year: 'numeric' }) });
}

export function formatPeriod(start: string | Date, end: string | Date): string {
  const s = parseNaiveDate(start);
  const e = parseNaiveDate(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const startStr = s.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const endStr = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

export function mapInvoiceStatus(raw?: string): InvoiceSubmissionStatus {
  if (!raw) return 'In Review';
  const s = raw.toLowerCase();
  if (s.includes('cancelled') || s.includes('canceled') || s.includes('duplicate')) return 'Cancelled';
  if (s.includes('paid')) return 'Paid';
  // consultant-invoices-api `waiting_payment` (+ legacy Airtable "waiting for payment").
  if (s.includes('waiting_payment') || s.includes('waiting for payment')) return 'Awaiting Payment';
  // consultant-invoices-api `to_fix` (+ legacy Airtable "sent back for corrections").
  if (s.includes('to_fix') || s.includes('sent back for corrections')) return 'Fix';
  // consultant-invoices-api `waiting_ops_approval` / `returned_by_accountant` — shown to the pilot as
  // "In Review" (agreed with Diana). Also the default for any unrecognised status.
  return 'In Review';
}

// Fixed-fee vs Time & Materials is carried authoritatively by the invoice's `billingModel`
// (consultant-invoices-api). Classify on that when present; only fall back to the legacy heuristic
// (no per-day flight total) for older invoices that predate the field — otherwise a mileage-only
// T&M invoice, whose `scans_panoramas` category is omitted from the totals, is misread as fixed-fee.
export function isFixedFeeInvoice(raw?: Invoice): boolean {
  if (!raw) return true;
  if (raw.billingModel) return raw.billingModel === 'fixed_fee';
  return raw.submittedTotals?.timeMaterialsFlightCosts == null;
}

export function getSectionSubtitle(section: InvoicingSection): string {
  if (section.items.length === 0) return '';
  const billable = section.items.filter((i) => i.status !== 'failed').length;
  const failed = section.items.filter((i) => i.status === 'failed').length;
  return failed > 0 ? `${billable} billable · ${failed} failed` : `${billable} billable`;
}

type SiteGroupable = {
  project: string;
  account: string;
  category: string;
  itemCount: number;
  isFailed?: boolean;
  isUnprocessed?: boolean;
  unprocessed?: boolean;
};

export function groupBySiteSummary<T extends SiteGroupable>(
  items: T[],
): Array<{ project: string; account: string; isFailed: boolean; isOnReview: boolean; summary: string }> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = `${item.project}\0${item.account}`;
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
  }
  return Array.from(groups.values()).map((group) => {
    const first = group[0];
    const allFailed = group.every((i) => i.isFailed);
    const anyUnprocessed = group.some((i) => i.isUnprocessed || i.unprocessed);
    const byCat = new Map<string, number>();
    for (const item of group) {
      byCat.set(item.category, (byCat.get(item.category) ?? 0) + (item.itemCount ?? 1));
    }
    const parts: string[] = [];
    for (const cat of CATEGORY_ORDER) {
      if (cat === 'video') {
        const videoN = byCat.get('video');
        if (videoN !== undefined) parts.push(videoN === 1 ? 'Video' : `${videoN} Videos`);
        const imageN = byCat.get('additional-images');
        if (imageN !== undefined) parts.push(imageN === 1 ? 'Image' : `${imageN} Images`);
        continue;
      }
      const n = byCat.get(cat);
      if (n === undefined) continue;
      switch (cat) {
        case 'scan':
          parts.push(n === 1 ? 'Scan' : `${n} Scans`);
          break;
        case 'pano':
          parts.push(`${n} Panorama${n !== 1 ? 's' : ''}`);
          break;
        case 'lidar':
          parts.push(n === 1 ? 'LiDAR' : `${n} LiDAR`);
          break;
        case 'gcp_services':
          parts.push(n === 1 ? 'GCP service' : `${n} GCP services`);
          break;
        case 'other':
          parts.push(n === 1 ? '1 other' : `${n} others`);
          break;
      }
    }
    return {
      project: first.project,
      account: first.account,
      isFailed: allFailed,
      isOnReview: !allFailed && anyUnprocessed,
      summary: parts.join(', '),
    };
  });
}

type FlightsCountable = { category: string; itemCount: number; isFailed?: boolean };

// Shared "N scans, N panos, N LiDAR, ..." subtitle builder for a T&M date group's non-GCP items —
// used by the pilot submission form, the office fix form, and the readonly detail view, which
// all group items by flight date and need the same per-category counts.
export function buildFlightsSubtitle<T extends FlightsCountable>(items: T[]): string {
  const scanCount = items.filter((i) => i.category === 'scan' && !i.isFailed).length;
  const panoCount = items.filter((i) => i.category === 'pano' && !i.isFailed).reduce((s, i) => s + i.itemCount, 0);
  const lidarCount = items.filter((i) => i.category === 'lidar' && !i.isFailed).length;
  const videoCount = items.filter((i) => i.category === 'video' && !i.isFailed).length;
  const imageCount = items.filter((i) => i.category === 'additional-images' && !i.isFailed).length;
  const otherCount = items.filter((i) => i.category === 'other' && !i.isFailed).length;
  const failedCount = items.filter((i) => i.isFailed).length;
  const parts: string[] = [];
  if (scanCount > 0) parts.push(`${scanCount} scan${scanCount !== 1 ? 's' : ''}`);
  if (panoCount > 0) parts.push(`${panoCount} pano${panoCount !== 1 ? 's' : ''}`);
  if (lidarCount > 0) parts.push(`${lidarCount} LiDAR`);
  if (videoCount > 0) parts.push(`${videoCount} video${videoCount !== 1 ? 's' : ''}`);
  if (imageCount > 0) parts.push(`${imageCount} image${imageCount !== 1 ? 's' : ''}`);
  if (otherCount > 0) parts.push(`${otherCount} other${otherCount !== 1 ? 's' : ''}`);
  if (failedCount > 0) parts.push(`${failedCount} not counted`);
  return parts.join(' · ');
}

// Shared "N services (· N failed)" subtitle builder for a T&M date group's GCP items.
export function buildGcpSubtitle<T extends { isFailed?: boolean }>(items: T[]): string {
  const billable = items.filter((i) => !i.isFailed).length;
  const failed = items.filter((i) => i.isFailed).length;
  return failed > 0
    ? `${billable} service${billable !== 1 ? 's' : ''} · ${failed} failed`
    : `${billable} service${billable !== 1 ? 's' : ''}`;
}

export function getItemRateInfo(item: InvoicingCostItem): string | undefined {
  if (item.rateInfo) return item.rateInfo;
  if (item.category === 'pano' && item.itemCount) {
    const n = item.itemCount;
    const countStr = `${n} pano${n !== 1 ? 's' : ''}`;
    if (item.amount) {
      const rate = parseFloat((item.amount / n).toFixed(2));
      return `${countStr} · $${rate}/pano`;
    }
    return countStr;
  }
  return undefined;
}

export function buildConsultantGcpRateInfo(hourlyRate?: number, timeInHours?: number): string | undefined {
  const hasRate = hourlyRate != null && hourlyRate !== 0;
  const hasHours = timeInHours != null;
  if (hasRate && hasHours) return `${timeInHours} hours · $${hourlyRate}/hour`;
  if (hasHours) return `${timeInHours} hours`;
  if (hasRate) return `$${hourlyRate}/hour`;
  return undefined;
}

// rate × hours, but only when both halves are actually filled in. A zero on either side means "not
// set yet" rather than a real zero — a rate of $0/hour or 0 confirmed hours would otherwise multiply
// the price away to $0, wiping out the amount the pilot/office entered by hand (which lives in the
// mission's `cost`). The single source of truth for both callers below, so they can't drift apart.
function deriveGcpRateAmount(hourlyRate?: number, timeInHours?: number): number | undefined {
  if (hourlyRate == null || hourlyRate === 0) return undefined;
  if (timeInHours == null || timeInHours === 0) return undefined;

  return hourlyRate * timeInHours;
}

// A GCP service item's price is only auto-calculated (and thus locked to that calculated value)
// once BOTH the consultant's hourly rate and confirmed hours are known — otherwise the amount
// can't be derived and the pilot/office still needs to enter it manually.
export function isGcpRateFullySet(hourlyRate?: number, timeInHours?: number): boolean {
  return deriveGcpRateAmount(hourlyRate, timeInHours) !== undefined;
}

// What to show and bill for a GCP service item: the calculated rate × hours where that's derivable,
// otherwise the mission's stored cost — which is where a manually entered price is kept.
export function getGcpItemAmount(cost: number, hourlyRate?: number, timeInHours?: number): number {
  return deriveGcpRateAmount(hourlyRate, timeInHours) ?? cost;
}

// GCP consultant rate basis (hourly rate + confirmed hours) for a submit/resubmit payload, so the
// rate breakdown round-trips on saved invoices (the service stores it verbatim, doesn't derive it).
// Omitted entirely when neither value is set.
export function toGcpCostBasis(
  hourlyRate?: number,
  timeInHours?: number,
): { hourlyRate?: number; timeInHours?: number } | undefined {
  if (hourlyRate == null && timeInHours == null) return undefined;

  return {
    ...(hourlyRate != null ? { hourlyRate } : {}),
    ...(timeInHours != null ? { timeInHours } : {}),
  };
}

// Whether an invoice carries its GCP materials/mileage on individual service items (the current
// model) instead of in the legacy lump-sum `gcpMaterialsCosts` field. Read from the invoice as
// loaded, so the answer stays stable while the office edits per-item rows — deleting them all
// included. Deriving it from live form state instead would flip a half-edited invoice back to the
// lump sum it was seeded with and send an aggregate no line item backs up.
export function hasPerItemGcpExtraCosts(raw?: Invoice): boolean {
  return raw?.associatedMissions.some((m) => (m.extraCosts ?? []).length > 0) ?? false;
}

// Whether `submittedTotals.gcpMaterialsCosts` is already covered by line items this app sums for
// itself, and so must not be added a second time as the legacy lump-sum row.
//
// The service rolls THREE different things into that one `gcp_materials` bucket: a gcp_services
// mission's extraCosts.materials, its extraCosts.mileage, and every T&M `mileage` row in
// timeMaterialsEntries (consultant-invoices-api 4d96a64). The first two are summed here from
// associatedMissions[].extraCosts and the third from timeMaterialsEntries, so once either source
// exists the aggregate is a duplicate of money already counted — and on a mileage-only T&M invoice
// it isn't even materials, so showing it as a "GCP Materials" line would be wrong twice over.
export function hasDerivedGcpMaterialsCosts(raw?: Invoice): boolean {
  return (
    hasPerItemGcpExtraCosts(raw) || (raw?.timeMaterialsEntries?.some((entry) => entry.category === 'mileage') ?? false)
  );
}

// The consultant-invoices-api day entries for one Time & Materials date: that day's flights line,
// its GCP services line and its mileage line, each omitted when there's no amount for it.
//
// `relatedMissionRefIds` is a BILLING DEDUP KEY, not just a "which missions did this cover"
// annotation: the service reads a reference as "this row already bills that mission's cost" and
// drops the mission's own `cost` from submittedTotals (build_submitted_totals — the referenced set
// is pooled across all rows, category ignored, and only ever applies to gcp_services missions).
//
// That's exactly why the day's GCP amount travels as its own `gcp_services` row here, referencing
// that day's GCP missions: the row supplies the amount, the missions' costs are deduped away, and
// each mission still carries its `cost`/`extraCosts` so the breakdown per service survives a
// round-trip. Because the amount no longer depends on the missions being counted, the mileage row
// can safely reference the whole day's work again. Sending mileage refs WITHOUT the gcp_services row
// is the one broken combination — the refs dedupe the GCP costs away and nothing replaces them.
//
// `gcpServices` and `gcpMaterials` are taken separately but sent as ONE row, per the service's
// request: it has no `gcp_materials` category, so a day's materials ride inside its services total.
// The consequence is the service's own to fix — it still counts the missions' `extraCosts` on top of
// the row (that add sits outside the dedup guard), so its totals read high by the materials until it
// skips extraCosts for a mission the row references. We keep sending extraCosts regardless: it is
// the only per-service breakdown, and dropping it would make a resubmit silently lose materials,
// since a combined row can't be split back into services and materials.
export function buildTimeMaterialsDayEntries({
  dateKey,
  flights,
  gcpServices,
  gcpMaterials,
  mileage,
  flightRefIds,
  gcpRefIds,
}: {
  dateKey: string;
  flights: number;
  gcpServices: number;
  gcpMaterials: number;
  mileage: number;
  flightRefIds: string[];
  gcpRefIds: string[];
}): TimeMaterialsEntryPayload[] {
  const entries: TimeMaterialsEntryPayload[] = [];
  const gcpTotal = gcpServices + gcpMaterials;

  if (flights)
    entries.push({ date: dateKey, category: 'scans_panoramas', cost: flights, relatedMissionRefIds: flightRefIds });
  if (gcpTotal)
    entries.push({ date: dateKey, category: 'gcp_services', cost: gcpTotal, relatedMissionRefIds: gcpRefIds });
  // The day's travel covers everything performed that day, so it references both sets.
  if (mileage)
    entries.push({
      date: dateKey,
      category: 'mileage',
      cost: mileage,
      relatedMissionRefIds: [...flightRefIds, ...gcpRefIds],
    });

  return entries;
}

// The mission ids a Time & Materials day entry references in `relatedMissionRefIds`. Missions with
// no airtableRecordId are skipped — there'd be nothing for the service to link the cost back to.
// Callers decide which missions belong to a given line (and whether failed ones count) before
// handing them over; this only unwraps the ids.
export function collectMissionRefIds(missions: Array<{ airtableRecordId?: string }>): string[] {
  return missions.filter((m) => !!m.airtableRecordId).map((m) => m.airtableRecordId as string);
}

// Total of several missions' `extraCosts`, for the day's `gcp_services` row that folds them in.
// Both halves count: the service pools materials and mileage into one bucket and is expected to skip
// a referenced mission's extraCosts as a whole, so the row has to carry as much as it displaces.
export function sumGcpExtraCosts(extraCosts: Array<{ materials?: number; mileage?: number } | undefined>): number {
  return extraCosts.reduce((sum, extra) => sum + (extra?.materials ?? 0) + (extra?.mileage ?? 0), 0);
}

// A GCP service item's edited materials/mileage rows, summed into the flat
// `{ materials, mileage }` shape the consultant-invoices-api stores on
// associatedMissions[].extraCosts. Shared by the pilot submit form and the office fix form so both
// send what the form currently holds. Zero/blank amounts are dropped, and an item with nothing
// entered yields `undefined` so no `extraCosts` key is sent for it at all.
export function toGcpExtraCosts(entries?: GcpMaterialEntry[]): { materials?: number; mileage?: number } | undefined {
  const sum = (type: GcpExtraCostType) =>
    (entries ?? []).filter((e) => e.type === type).reduce((s, e) => s + (parseFloat(e.amount || '0') || 0), 0);
  const materials = sum('materials');
  const mileage = sum('mileage');
  const result: { materials?: number; mileage?: number } = {};

  if (materials > 0) result.materials = materials;
  if (mileage > 0) result.mileage = mileage;

  return Object.keys(result).length ? result : undefined;
}

export function formatApiDateCompact(date: string | Date): string {
  return parseNaiveDate(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

export type GcpFixItem = AssociatedMission & { id: string };

export type TimeMaterialsDateGroup = {
  dateKey: string;
  displayDate: string;
  scanPanoLidarItems: AssociatedMission[];
  gcpItems: GcpFixItem[];
};

// The consultant-invoices-api carries per-day Time & Materials amounts in `timeMaterialsEntries`
// (one row per date+category). This unpacks them into the `{ [dateKey]: amount }` maps the
// "Scans, panoramas price" (flights) and "Mileage" per-date fields expect — shared by the readonly
// detail view (display) and the office fix form (prefill). Amounts are kept as strings because the
// per-date fields are text inputs.
export function buildTimeMaterialsAmountsByDate(entries?: TimeMaterialsEntry[]): {
  flightsCostsByDate: Record<string, string>;
  mileageByDate: Record<string, string>;
} {
  const flightsCostsByDate: Record<string, string> = {};
  const mileageByDate: Record<string, string> = {};
  for (const entry of entries ?? []) {
    // Matched explicitly rather than "mileage or else flights": a `gcp_services` row must land in
    // NEITHER map. Its amount is shown per item from associatedMissions, so bucketing it as flights
    // would both display it in the day's "Scans, panoramas price" field and count it twice.
    const target =
      entry.category === 'mileage' ? mileageByDate : entry.category === 'scans_panoramas' ? flightsCostsByDate : null;

    if (!target) continue;
    // Sum defensively in case a date+category ever appears more than once (normally one per pair).
    const prev = parseFloat(target[entry.date] ?? '0') || 0;
    target[entry.date] = String(prev + (entry.cost ?? 0));
  }
  return { flightsCostsByDate, mileageByDate };
}

const CATEGORY_CONFIG = [
  { id: 'scan', label: 'Scans' },
  { id: 'pano', label: 'Additional Panoramas' },
  { id: 'lidar', label: 'LiDAR' },
  { id: 'gcp_services', label: 'GCP Services' },
  { id: 'video', label: 'Videos & Images' },
  { id: 'other', label: 'Misc' },
];

// Form-state keys for one list of line items, derived from each mission's own Airtable record id
// instead of its position in the list.
//
// These ids key per-item state that outlives a re-render — GCP price overrides
// (useGcpItemPrices) and materials/mileage rows (useGcpMaterialEntries) — while every list they
// index is a *filtered* one: by the submit form's date range, by category, by date. A positional
// key re-binds to a different mission the moment the list shrinks or grows, and the state parked
// under it stays behind on what is now someone else's row. That is how one invoice went out with
// every GCP price shifted a row down: the pilot narrowed the period after the form had loaded, the
// item that had been first dropped off, and all the seeded prices slid onto the wrong services.
//
// `prefix` keeps ids apart across the lists that share one state map (a category, or a date); the
// occurrence suffix covers the same record appearing twice in one list. An item with no record id
// has nothing stable to key on and falls back to its position — acceptable only because such an
// item can't reach a submit payload anyway (every builder filters on airtableRecordId).
export function buildStableItemIds(prefix: string, recordIds: Array<string | undefined>): string[] {
  const occurrences = new Map<string, number>();

  return recordIds.map((recordId, index) => {
    const base = recordId ?? `index${index}`;
    const seen = occurrences.get(base) ?? 0;
    occurrences.set(base, seen + 1);

    return seen === 0 ? `${prefix}-${base}` : `${prefix}-${base}-${seen}`;
  });
}

export function buildSections(raw: Invoice): InvoicingSection[] {
  const sections: InvoicingSection[] = [];
  for (const cat of CATEGORY_CONFIG) {
    const rawItems = raw.associatedMissions.filter((c) => getCategoryGroupId(c.category) === cat.id);
    if (rawItems.length === 0) continue;
    const sorted = [...rawItems].sort((a, b) => b.flightDate.localeCompare(a.flightDate));
    const ids = buildStableItemIds(
      cat.id,
      sorted.map((c) => c.airtableRecordId),
    );
    const items: InvoicingCostItem[] = sorted.map((c, idx) => ({
      id: ids[idx],
      airtableRecordId: c.airtableRecordId,
      gcpServiceId: c.gcpServiceId,
      date: parseNaiveDate(c.flightDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      customer: c.account,
      siteName: c.project,
      status: (c.isFailed ? 'failed' : c.isUnprocessed ? 'on_review' : 'billable') as ItemStatus,
      amount: getGcpItemAmount(c.cost, c.consultantGcpServiceHourlyRate, c.consultantGcpServiceTimeInHours),
      itemCount: c.itemCount,
      category: c.category,
      consultantGcpServiceHourlyRate: c.consultantGcpServiceHourlyRate,
      consultantGcpServiceTimeInHours: c.consultantGcpServiceTimeInHours,
      rateInfo:
        cat.id === 'gcp_services' && !c.isFailed
          ? buildConsultantGcpRateInfo(c.consultantGcpServiceHourlyRate, c.consultantGcpServiceTimeInHours)
          : undefined,
      extraCosts: c.extraCosts?.map((m) => ({ description: m.description, amount: m.amount })),
    }));
    const itemsTotal = items
      .filter((i) => i.status !== 'failed')
      .reduce((s, i) => s + (i.amount ?? 0) + (i.extraCosts ?? []).reduce((ms, m) => ms + m.amount, 0), 0);
    sections.push({ id: cat.id, label: cat.label, total: itemsTotal, items });
  }
  return sections;
}

export type CustomRow = {
  id: string;
  label: string;
  desc: string;
  amount: number;
  fixKey?: keyof InvoicingFixValues;
};

export function buildCustomRows(raw: Invoice): CustomRow[] {
  const rows: CustomRow[] = [];
  // TODO: Delete it once switched to new consultant invoices api. Older invoices used one lump-sum
  // "GCP Materials" cost; on the new backend the same aggregate is instead derived from per-item
  // extra costs and T&M mileage rows, both of which are already counted from the line items
  // themselves — so the lump-sum row is only a real, separate cost when neither source exists.
  const isGcpMaterialsAlreadyCounted = hasDerivedGcpMaterialsCosts(raw);
  const totals = raw.submittedTotals;
  if (!isGcpMaterialsAlreadyCounted && totals?.gcpMaterialsCosts !== undefined)
    rows.push({
      id: 'gcpmat',
      label: 'GCP Materials',
      desc: 'GCP setup supply expenses',
      amount: totals.gcpMaterialsCosts,
      fixKey: 'gcpMaterialsCosts',
    });
  if (totals?.equipmentLeasingExpenses !== undefined)
    rows.push({
      id: 'eqlease',
      label: 'Equipment Leasing',
      desc: 'TraceAir equipment rental expenses',
      amount: -totals.equipmentLeasingExpenses,
      fixKey: 'equipmentLeasingExpenses',
    });
  return rows;
}

export function buildFixDateGroups(raw: Invoice): TimeMaterialsDateGroup[] {
  const byDate: Record<string, AssociatedMission[]> = {};
  for (const m of raw.associatedMissions) {
    (byDate[m.flightDate] ??= []).push(m);
  }
  return Object.entries(byDate)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dateKey, items]) => {
      const gcpMissions = items.filter((i) => i.category === 'gcp_services');
      const gcpIds = buildStableItemIds(
        dateKey,
        gcpMissions.map((i) => i.airtableRecordId),
      );
      const gcpItems = gcpMissions.map((item, idx) => ({ ...item, id: gcpIds[idx] }));
      return {
        dateKey,
        displayDate: formatApiDateCompact(dateKey),
        scanPanoLidarItems: items.filter((i) => i.category !== 'gcp_services'),
        gcpItems,
      };
    });
}
